/**
 * Client-side UI preferences. Nothing here is business data.
 *
 * The theme value MUST agree with the pre-paint script in `index.html`: same
 * localStorage key, same `data-theme` attribute. If they disagree the page paints one
 * theme and React immediately flips it — the flash the pre-paint script exists to stop.
 */
import { create } from 'zustand';

export type ThemeChoice = 'light' | 'dark' | 'system';

const THEME_KEY = 'agenterp.theme';
const SIDEBAR_KEY = 'agenterp.sidebar';

function systemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readThemeChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(THEME_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

function applyTheme(resolved: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
  document.documentElement.style.colorScheme = resolved;
}

interface PreferencesState {
  theme: ThemeChoice;
  resolvedTheme: 'light' | 'dark';
  sidebarCollapsed: boolean;
  mobileNavOpen: boolean;
  setTheme: (choice: ThemeChoice) => void;
  toggleTheme: () => void;
  toggleSidebar: () => void;
  setMobileNavOpen: (open: boolean) => void;
}

export const usePreferences = create<PreferencesState>((set, get) => ({
  theme: readThemeChoice(),
  resolvedTheme: readThemeChoice() === 'system' ? systemTheme() : (readThemeChoice() as 'light' | 'dark'),
  // Collapsed is the accepted resting state (the mockups' rail is icon-only), so an UNSET
  // preference means collapsed. It previously defaulted to expanded, which shipped every
  // first-time user the opt-in variant of a design they had not opted into.
  sidebarCollapsed:
    typeof window === 'undefined' ? true : window.localStorage.getItem(SIDEBAR_KEY) !== 'expanded',
  mobileNavOpen: false,

  setTheme: (choice) => {
    const resolved = choice === 'system' ? systemTheme() : choice;
    if (typeof window !== 'undefined') {
      if (choice === 'system') window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, choice);
    }
    applyTheme(resolved);
    set({ theme: choice, resolvedTheme: resolved });
  },

  toggleTheme: () => {
    get().setTheme(get().resolvedTheme === 'dark' ? 'light' : 'dark');
  },

  toggleSidebar: () =>
    set((state) => {
      const next = !state.sidebarCollapsed;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'expanded');
      }
      return { sidebarCollapsed: next };
    }),

  setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
}));

/** Follow the OS when the user has not made an explicit choice. */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (): void => {
    const { theme, setTheme } = usePreferences.getState();
    if (theme === 'system') setTheme('system');
  };
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
